import { AlertTriangle, CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Copy, Crown, ExternalLink, Medal, Search, Shield, Trophy, Users, Wifi, WifiOff } from "lucide-react";
import React from "react";
import { Link, NavLink } from "react-router-dom";
import { minecraftAvatarUrl, type PlayerRow } from "../api";
import { SocialIconLinks } from "./social";
import { useLive } from "./live";

export function Navbar() {
  const links: Array<[string, string]> = [
    ["/leaderboard", "Clasificacion"],
    ["/players", "Jugadores"],
    ["/teams", "Equipos"],
    ["/matches", "Partidas"],
    ["/events", "Eventos"],
    ["/status", "Estado"],
    ["/contacto", "Contacto"]
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-line/80 bg-ink/88 backdrop-blur-xl">
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
        <Link to="/" className="flex items-center text-white" aria-label="DarkLabs">
          <img className="h-[66px] w-[256px] max-w-[70vw]" src="/brand/darklabs.png" alt="DarkLabs" width={256} height={66} style={{ imageRendering: "pixelated" }} />
        </Link>
        <div className="hidden items-center gap-1 md:flex">
          {links.map(([href, label]) => (
            <NavLink key={href} to={href} className={({ isActive }) => `rounded-md border px-3 py-2 text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-electric ${isActive ? "border-electric/45 bg-electric/10 text-white" : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white"}`}>
              {label}
            </NavLink>
          ))}
        </div>
        <LiveIndicator />
      </nav>
    </header>
  );
}

export function MobileNavbar() {
  const links: Array<[string, string]> = [["/leaderboard", "Top"], ["/players", "Jugadores"], ["/teams", "Equipos"], ["/events", "Eventos"], ["/contacto", "Contacto"]];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 border-t border-line bg-ink/95 p-2 backdrop-blur md:hidden">
      {links.map(([href, label]) => (
        <NavLink key={href} to={href} className={({ isActive }) => `rounded-md px-2 py-2 text-center text-xs font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-electric ${isActive ? "bg-electric text-ink" : "text-slate-300"}`}>
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Footer() {
  const links: Array<[string, string]> = [["/leaderboard", "Clasificacion"], ["/events", "Eventos"], ["/contacto", "Contacto"]];
  return (
    <footer className="border-t border-line px-4 pb-24 pt-8 md:pb-8">
      <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-[1fr_auto_auto] md:items-center">
        <div>
          <Link to="/" className="inline-flex items-center text-white" aria-label="DarkLabs">
            <img className="h-auto w-44" src="/brand/darklabs.png" alt="DarkLabs" width={176} height={46} style={{ imageRendering: "pixelated" }} />
          </Link>
          <p className="mt-3 max-w-xl text-sm text-slate-500">DarkLabs - Eventos, minijuegos y experiencias competitivas de Minecraft.</p>
        </div>
        <nav className="flex flex-wrap gap-3 text-sm font-bold">
          {links.map(([href, label]) => <Link key={href} className="rounded-md px-2 py-2 text-slate-300 hover:bg-white/5 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-electric" to={href}>{label}</Link>)}
        </nav>
        <SocialIconLinks />
      </div>
    </footer>
  );
}

export function Hero({ title, subtitle, children }: { title: React.ReactNode; subtitle: string; children?: React.ReactNode }) {
  return (
    <section className="hero-grid relative overflow-hidden border-b border-line">
      <div className="mx-auto grid min-h-[420px] max-w-7xl content-center gap-8 px-4 py-14 md:grid-cols-[1.2fr_.8fr]">
        <div className="max-w-3xl">
          <p className="mb-4 inline-flex rounded-md border border-electric/30 bg-electric/10 px-3 py-1 text-sm font-semibold text-electric">Temporada activa</p>
          <h1 className="leading-none text-white">{title}</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">{subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-3">{children}</div>
        </div>
        <div className="hidden place-items-center md:grid">
          <Podium compact />
        </div>
      </div>
    </section>
  );
}

export function StatCard({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return <div className="rounded-lg border border-line bg-panel/80 p-5 shadow-glow"><div className="text-sm text-slate-400">{label}</div><div className="mt-2 text-3xl font-black text-white">{value}</div>{hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}</div>;
}

export function MinecraftAvatar({ player, size = "md" }: { player: Pick<PlayerRow, "uuid" | "avatar_url" | "current_name">; size?: "sm" | "md" | "lg" }) {
  const [source, setSource] = React.useState<"uuid" | "name" | "initials">("uuid");
  const cls = size === "lg" ? "h-24 w-24" : size === "sm" ? "h-10 w-10" : "h-14 w-14";
  const src = player.avatar_url || (source === "uuid" ? minecraftAvatarUrl(player.uuid) : source === "name" ? minecraftAvatarUrl(player.current_name) : "");
  if (source === "initials") {
    return <span className={`${cls} grid place-items-center rounded-md border border-white/10 bg-electric/15 font-black text-electric`}>{player.current_name.slice(0, 2).toUpperCase()}</span>;
  }
  return <img className={`${cls} rounded-md border border-white/10 bg-slate-900 object-cover`} src={src} alt={`Avatar de ${player.current_name}`} loading="lazy" onError={() => setSource(source === "uuid" ? "name" : "initials")} />;
}

export const PlayerAvatar = MinecraftAvatar;

export function PlayerCard({ player, children }: { player: PlayerRow; children?: React.ReactNode }) {
  return (
    <Link to={`/player/${player.current_name}`} className="group flex items-center gap-4 rounded-lg border border-line bg-panel/70 p-4 transition hover:border-electric/50 hover:bg-panel">
      <PlayerAvatar player={player} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-bold text-white group-hover:text-electric">{player.current_name}</div>
        <div className="text-sm text-slate-400">{player.team_name ?? "Sin equipo"} · #{player.rank ?? "-"}</div>
        {children}
      </div>
      <div className="text-right">
        <div className="font-black text-white">{player.points}</div>
        <div className="text-xs text-slate-500">puntos</div>
      </div>
    </Link>
  );
}

export function TeamCard({ team }: { team: Record<string, unknown> }) {
  return (
    <Link to={`/team/${team.slug}`} className="rounded-lg border border-line bg-panel/70 p-5 transition hover:border-violet/60">
      <div className="flex items-center gap-3">
        <TeamLogo team={team} size="md" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-black text-white">{String(team.name)}</div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-400">
            {Boolean(team.short_name) && Boolean(team.show_short_name ?? true) && <span>{String(team.short_name)}</span>}
            <span>{String(team.members ?? 0)} miembros</span>
          </div>
        </div>
      </div>
      {Boolean(team.leader_name) && <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-amber-300/30 bg-amber-300/10 px-2 py-1 text-sm text-amber-100"><Crown size={15} aria-hidden="true" />{String(team.leader_name)}</div>}
      <div className="mt-5 grid grid-cols-2 gap-3 text-sm"><StatMini label="Puntos" value={String(team.points ?? 0)} /><StatMini label="Victorias" value={String(team.wins ?? 0)} /></div>
    </Link>
  );
}

export function TeamLogo({ team, size = "md" }: { team: Record<string, unknown>; size?: "sm" | "md" | "lg" | "xl" }) {
  const cls = size === "xl" ? "h-28 w-28" : size === "lg" ? "h-20 w-20" : size === "sm" ? "h-8 w-8" : "h-16 w-16";
  const shape = String(team.logo_shape ?? "square");
  const radius = shape === "circle" ? "rounded-full" : shape === "rounded" ? "rounded-lg" : "rounded-md";
  const logo = team.logo_url ? String(team.logo_url) : "";
  const color = String(team.primary_color ?? team.accent_color ?? "#38bdf8");
  if (logo) {
    return (
      <img
        className={`${cls} ${radius} border border-white/10 bg-slate-950 object-contain`}
        src={logo}
        alt={`Logo de ${String(team.name ?? "equipo")}`}
        loading="lazy"
        style={{ imageRendering: "pixelated" }}
      />
    );
  }
  return <span className={`${cls} ${radius} grid place-items-center border border-white/10 font-black text-ink`} style={{ background: color }}>{String(team.short_name ?? team.name ?? "TM").slice(0, 3).toUpperCase()}</span>;
}

export function PixelBanner({ src, fallback, overlay, className = "", alt = "Banner", positionX = 50, positionY = 50, children }: { src?: string | null; fallback: string; overlay?: string; className?: string; alt?: string; positionX?: number; positionY?: number; children?: React.ReactNode }) {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return <div className={`relative isolate overflow-hidden ${className}`} style={{ background: fallback }}>{children && <div className="relative z-10">{children}</div>}</div>;
  }
  return (
    <div className={`relative isolate overflow-hidden ${className}`}>
      <img
        className="absolute inset-0 z-0 h-full w-full object-cover"
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{ imageRendering: "pixelated", objectPosition: `${positionX}% ${positionY}%` }}
      />
      {overlay && <div className="absolute inset-0 z-[1]" style={{ background: overlay }} />}
      {children && <div className="relative z-10">{children}</div>}
    </div>
  );
}

export function RoleBadge({ role, teamColor }: { role?: string | null; teamColor?: string | null }) {
  const normalized = String(role ?? "member");
  const labels: Record<string, string> = { leader: "Lider", captain: "Capitan", member: "Miembro", substitute: "Suplente", reserve: "Reserva", staff: "Staff", inactive: "Inactivo" };
  const Icon = normalized === "leader" ? Crown : normalized === "captain" ? Shield : null;
  return <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs font-bold text-slate-100" style={{ color: normalized === "leader" ? (teamColor ?? "#facc15") : undefined }}>{Icon && <Icon size={13} aria-hidden="true" />}{labels[normalized] ?? normalized}</span>;
}

function StatMini({ label, value }: { label: string; value: string }) {
  return <div className="rounded-md bg-white/5 p-3"><div className="text-slate-500">{label}</div><div className="font-black text-white">{value}</div></div>;
}

export function Podium({ players = [], compact = false }: { players?: PlayerRow[]; compact?: boolean }) {
  const top = players.slice(0, 3);
  return (
    <div className={`grid ${compact ? "w-full max-w-sm" : ""} grid-cols-3 items-end gap-3`}>
      {[top[1], top[0], top[2]].map((player, index) => {
        const place = [2, 1, 3][index]!;
        const height = compact
          ? place === 1 ? "h-48" : place === 2 ? "h-40" : "h-36"
          : place === 1 ? "h-56" : place === 2 ? "h-48" : "h-44";
        const avatarSize = place === 1 && !compact ? "lg" : "md";
        return (
          <div key={place} className={`flex flex-col items-center justify-center rounded-lg border border-line bg-panel/80 p-3 text-center ${height}`}>
            {player ? <PlayerAvatar player={player} size={avatarSize} /> : <span className="h-14 w-14 rounded-md border border-white/10 bg-white/5" />}
            <div className="mt-3 w-full truncate text-sm text-slate-200">{player?.current_name ?? "Esperando"}</div>
            <Trophy className="mt-2 text-electric" size={22} />
            <div className="mt-1 text-2xl font-black text-white">#{place}</div>
          </div>
        );
      })}
    </div>
  );
}

export function LeaderboardTable({ players }: { players: PlayerRow[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead className="bg-white/5 text-slate-400"><tr><th className="p-4">#</th><th>Jugador</th><th>Equipo</th><th>Puntos</th><th>Eventos</th><th>Kills</th><th title="Ayudo a eliminar a otro jugador sin realizar el golpe final">Asistencias</th><th>K/D</th><th>KDA</th><th>Win rate</th></tr></thead>
        <tbody>{players.map((player) => <tr key={player.uuid} className="border-t border-line bg-panel/40"><td className="p-4 font-bold text-slate-300">{player.rank}</td><td><Link className="flex items-center gap-3 font-bold text-white" to={`/player/${player.current_name}`}><PlayerAvatar player={player} size="sm" />{player.current_name}</Link></td><td>{player.team_name ?? "-"}</td><td>{player.points}</td><td>{player.events_played ?? 0}</td><td>{player.kills}</td><td>{player.assists ?? 0}</td><td>{player.kd.toFixed(2)}</td><td>{(player.kda ?? 0).toFixed(2)}</td><td>{player.win_rate.toFixed(1)}%</td></tr>)}</tbody>
      </table>
    </div>
  );
}

export function LeaderboardMobileCard({ player }: { player: PlayerRow }) {
  return (
    <PlayerCard player={player}>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-400">
        <span>Kills <strong className="block text-white">{player.kills}</strong></span>
        <span>Asist. <strong className="block text-white">{player.assists ?? 0}</strong></span>
        <span>KDA <strong className="block text-white">{(player.kda ?? 0).toFixed(2)}</strong></span>
      </div>
    </PlayerCard>
  );
}

export function MatchCard({ match }: { match: Record<string, unknown> }) {
  return <Link to={`/match/${match.id}`} className="block rounded-lg border border-line bg-panel/70 p-5 hover:border-cyan/60"><div className="flex justify-between gap-4"><div><div className="font-black text-white">{String(match.game_name ?? "Partida")}</div><div className="text-sm text-slate-400">{String(match.external_id ?? match.id)}</div></div><RankBadge label={String(match.status)} /></div><div className="mt-4 text-sm text-slate-500">{String(match.started_at ?? "")}</div></Link>;
}

export function EventCard({ event }: { event: Record<string, unknown> }) {
  const slug = String(event.slug ?? "");
  const registration = event.registration as Record<string, unknown> | undefined;
  const registrationOpen = registration?.enabled === true && registration.status === "open" && typeof registration.url === "string";
  const participants = Number(event.participant_count ?? event.participants ?? registration?.participantCount ?? 0);
  const remaining = registration?.remainingSlots === null || registration?.remainingSlots === undefined ? null : Number(registration.remainingSlots);
  const shortDescription = String(event.shortDescription ?? event.short_description ?? event.description ?? "");
  const edition = event.edition ? String(event.edition) : "";
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-lg border border-line bg-panel/70 transition hover:border-electric/50">
      <PixelBanner
        className="h-36 bg-white/5 p-3"
        src={String(event.bannerUrl ?? event.banner_url ?? "") || null}
        fallback={eventFallback(event)}
        overlay="linear-gradient(180deg, rgba(2,6,23,.35), rgba(2,6,23,.88))"
        alt={`Banner de ${String(event.name ?? "evento")}`}
        positionX={Number(event.bannerPositionX ?? event.banner_position_x ?? 50)}
        positionY={Number(event.bannerPositionY ?? event.banner_position_y ?? 50)}
      >
        <div className="flex h-full flex-col justify-between">
          <div className="flex items-start justify-between gap-2">
            <RankBadge label={eventStatusLabel(String(event.effectiveStatus ?? event.effective_status ?? event.status ?? "upcoming"))} />
            {Boolean(event.featured) && <span className="rounded-md border border-amber-300/30 bg-amber-300/10 px-2 py-1 text-xs font-bold text-amber-100">Destacado</span>}
          </div>
          <EventCountdown event={event} compact />
        </div>
      </PixelBanner>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-3">
          <EventBadge event={event} />
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-black text-white">{String(event.name ?? "Evento")}</h3>
            {edition && <p className="text-sm text-electric">{edition}</p>}
          </div>
        </div>
        {shortDescription && <p className="mt-3 line-clamp-3 text-sm text-slate-400">{shortDescription}</p>}
        <div className="mt-4 grid gap-2 text-sm text-slate-300">
          <EventDate event={event} />
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1"><Users size={15} />{participants} participantes</span>
            {remaining !== null && <span>{remaining > 0 ? `${remaining} cupos libres` : "Cupo completo"}</span>}
          </div>
        </div>
        <div className="mt-auto flex flex-wrap gap-2 pt-5">
          <Link className="btn-primary flex-1 justify-center px-3 py-2" to={`/events/${slug}`}>Ver evento</Link>
          {registrationOpen && <a className="btn-secondary flex-1 justify-center px-3 py-2" href={String(registration.url)} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} />{String(registration.buttonText ?? "Registrarme")}</a>}
        </div>
      </div>
    </article>
  );
}

export function EventBadge({ event, size = "md" }: { event: Record<string, unknown>; size?: "sm" | "md" | "lg" }) {
  const cls = size === "lg" ? "h-20 w-20" : size === "sm" ? "h-10 w-10" : "h-14 w-14";
  const badge = String(event.badgeUrl ?? event.badge_url ?? "");
  const [failed, setFailed] = React.useState(false);
  if (badge && !failed) {
    return <img className={`${cls} rounded-md border border-white/10 bg-slate-950 object-contain`} src={badge} alt={`Insignia de ${String(event.name ?? "evento")}`} loading="lazy" onError={() => setFailed(true)} style={{ imageRendering: "pixelated" }} />;
  }
  return <span className={`${cls} grid place-items-center rounded-md border border-white/10 bg-white/5 font-black`} style={{ color: String(event.primaryColor ?? event.primary_color ?? "#38bdf8") }}>{String(event.name ?? "EV").slice(0, 2).toUpperCase() || <Medal />}</span>;
}

export function RankBadge({ label }: { label: string }) {
  return <span className="inline-flex rounded-md border border-electric/30 bg-electric/10 px-2 py-1 text-xs font-bold uppercase text-electric">{label}</span>;
}

export function EventDate({ event }: { event: Record<string, unknown> }) {
  if (event.dateConfirmed === false || event.date_confirmed === 0) return <span className="inline-flex items-center gap-1"><CalendarDays size={15} />Fecha por confirmar</span>;
  const startsAt = String(event.startsAt ?? event.starts_at ?? "");
  if (!startsAt) return <span className="inline-flex items-center gap-1"><CalendarDays size={15} />Fecha por confirmar</span>;
  const timezone = String(event.timezone ?? "America/Mexico_City");
  const date = new Date(startsAt);
  if (Number.isNaN(date.getTime())) return <span className="inline-flex items-center gap-1"><CalendarDays size={15} />Fecha por confirmar</span>;
  return <span className="inline-flex items-center gap-1"><CalendarDays size={15} />{new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short", timeZone: timezone }).format(date)} · {timezoneLabel(timezone)}</span>;
}

export function EventCountdown({ event, compact = false }: { event: Record<string, unknown>; compact?: boolean }) {
  const [now, setNow] = React.useState(Date.now());
  const serverTime = Date.parse(String(event.serverTime ?? event.server_time ?? ""));
  const offset = Number.isFinite(serverTime) ? serverTime - Date.now() : 0;
  const status = String(event.effectiveStatus ?? event.effective_status ?? event.status ?? "upcoming");
  const enabled = event.countdownEnabled === true || event.countdown_enabled === 1;
  React.useEffect(() => {
    if (!enabled) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [enabled]);
  if (!enabled || event.dateConfirmed === false || event.date_confirmed === 0 || status === "cancelled" || status === "archived" || status === "draft") return null;
  const serverNow = now + offset;
  const registration = event.registration as Record<string, unknown> | undefined;
  const targetRaw = event.countdownTarget === "registration_close" || event.countdown_target === "registration_close" ? registration?.closesAt ?? event.registration_closes_at : event.startsAt ?? event.starts_at;
  const endsAt = Date.parse(String(event.endsAt ?? event.ends_at ?? ""));
  if (status === "live" && Number.isFinite(endsAt) && endsAt > serverNow) return <CountdownShell compact={compact} label="Termina en" ms={endsAt - serverNow} />;
  if (status === "live") return <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-300"><Clock size={15} />En vivo</span>;
  if (status === "finished") return <span className="inline-flex items-center gap-1 text-sm font-bold text-slate-300"><Clock size={15} />Finalizado</span>;
  const target = Date.parse(String(targetRaw ?? ""));
  if (!Number.isFinite(target) || target <= serverNow) return null;
  return <CountdownShell compact={compact} label={target - serverNow < 86_400_000 ? "Comienza en" : "Faltan"} ms={target - serverNow} />;
}

function CountdownShell({ label, ms, compact }: { label: string; ms: number; compact: boolean }) {
  const text = countdownText(ms, label);
  const showSeconds = ms < 86_400_000;
  return <span className={`inline-flex items-center gap-1 font-bold text-white ${compact ? "text-sm" : "text-base"}`} aria-live={showSeconds ? "off" : "polite"}><Clock size={15} />{text}</span>;
}

function countdownText(ms: number, label: string) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 7) return `${label} ${days} dias`;
  if (days >= 1) return `${label} ${days} dias y ${hours} horas`;
  return `${label} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function eventStatusLabel(status: string) {
  const labels: Record<string, string> = { draft: "Borrador", upcoming: "Proximo", live: "En vivo", finished: "Finalizado", cancelled: "Cancelado", archived: "Archivado" };
  return labels[status] ?? status;
}

function timezoneLabel(timezone: string) {
  return timezone === "America/Mexico_City" ? "Hora de Ciudad de Mexico" : timezone;
}

function eventFallback(event: Record<string, unknown>) {
  const primary = String(event.primaryColor ?? event.primary_color ?? "#38bdf8");
  const secondary = String(event.secondaryColor ?? event.secondary_color ?? "#111827");
  return `radial-gradient(circle at 20% 15%, ${primary}66, transparent 28%), linear-gradient(135deg, ${primary}44, ${secondary} 58%, #020617)`;
}

export function LiveIndicator() {
  const live = useLive();
  const active = live.state === "live";
  return <div className={`hidden items-center gap-2 rounded-md border px-3 py-2 text-xs font-bold md:flex ${active ? "border-emerald-400/30 text-emerald-300" : "border-amber-400/30 text-amber-300"}`}>{active ? <Wifi size={14} /> : <WifiOff size={14} />}{live.state}</div>;
}

export function SearchBar({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <label className="relative block"><Search className="pointer-events-none absolute left-3 top-3 text-slate-500" size={18} /><input className="w-full rounded-md border border-line bg-panel px-10 py-3 text-white outline-none focus:border-electric" value={value} onChange={(event) => onChange(event.target.value)} placeholder="Buscar" /></label>;
}

export function FilterSelect({ value, onChange, options, label }: { value: string; onChange: (value: string) => void; options: Array<[string, string]>; label: string }) {
  return <label className="grid gap-1 text-sm text-slate-400">{label}<select className="rounded-md border border-line bg-panel px-3 py-3 text-white outline-none focus:border-electric" value={value} onChange={(event) => onChange(event.target.value)}>{options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}</select></label>;
}

export function Pagination({ page, total, limit, onPage }: { page: number; total: number; limit: number; onPage: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  return <div className="flex items-center justify-end gap-2"><button className="icon-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Pagina anterior"><ChevronLeft /></button><span className="text-sm text-slate-400">{page} / {pages}</span><button className="icon-btn" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Pagina siguiente"><ChevronRight /></button></div>;
}

export function Skeleton({ className = "h-24" }: { className?: string }) {
  return <div className={`${className} animate-pulse rounded-lg border border-line bg-white/5`} />;
}

export function EmptyState({ title = "Sin datos", text = "Aun no hay estadisticas para mostrar." }) {
  return <div className="rounded-lg border border-line bg-panel/60 p-8 text-center"><Check className="mx-auto text-slate-500" /><h3 className="mt-4 font-black text-white">{title}</h3><p className="mt-2 text-slate-400">{text}</p></div>;
}

export function ErrorState({ message }: { message: string }) {
  return <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-5 text-red-100"><AlertTriangle className="mb-2" />{message}</div>;
}

export function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  return <button className="btn-secondary" onClick={() => void navigator.clipboard.writeText(value).then(() => setCopied(true))}><Copy size={16} />{copied ? "Copiado" : "Copiar"}</button>;
}

export function Breadcrumbs({ items }: { items: Array<[string, string]> }) {
  return <div className="mb-5 text-sm text-slate-500">{items.map(([href, label], index) => <React.Fragment key={href}><Link className="hover:text-electric" to={href}>{label}</Link>{index < items.length - 1 && " / "}</React.Fragment>)}</div>;
}

export function StatChart({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  return <div className="flex h-24 items-end gap-2">{values.map((value, index) => <div key={index} className="flex-1 rounded-t bg-gradient-to-t from-violet to-electric" style={{ height: `${Math.max(8, (value / max) * 100)}%` }} />)}</div>;
}

export function Tooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return <span className="group relative inline-flex">{children}<span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white group-hover:block">{label}</span></span>;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error?: Error }> {
  state: { error?: Error } = {};
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) return <main className="mx-auto min-h-screen max-w-3xl px-4 py-20"><ErrorState message={this.state.error.message} /></main>;
    return this.props.children;
  }
}
